/**
 * Helpers shared by the three imputación tables (team, admin, personal) for
 * the Redmine overlay: the hours actually logged in Redmine travel on each
 * allocation row next to the Manager's planned ones.
 */

export type RealStatus = 'ok' | 'under' | 'over' | 'unmapped' | 'unlinked' | 'unplanned' | null | undefined

export interface RedmineRowFields {
    daily_real?: { [date: string]: number }
    total_real?: number | null
    real_available?: boolean
    real_status?: RealStatus
    is_real_only?: boolean
    redmine_project_id?: number | null
    redmine_project_name?: string | null
}

export interface RedmineTableFields {
    redmine_available?: boolean
    redmine_message?: string | null
    unmatched_users?: string[]
    as_of?: string | null
}

/** Colour for the real-hours figure of a row. */
export const realStatusColor = (status: RealStatus): string => {
    switch (status) {
        case 'ok': return '#2E7D32'
        case 'under': return '#E65100'
        case 'over': return '#C62828'
        case 'unplanned': return '#1565C0'
        default: return '#9E9E9E'
    }
}

/** Short Spanish label for a row's real status, for tooltips. */
export const realStatusLabel = (status: RealStatus): string => {
    switch (status) {
        case 'ok': return 'Imputación real acorde a lo planificado'
        case 'under': return 'Imputado menos de lo planificado hasta hoy'
        case 'over': return 'Imputado más de lo planificado hasta hoy'
        case 'unplanned': return 'Horas en Redmine sin planificación en el Manager'
        case 'unmapped': return 'Usuario sin equivalente en Redmine'
        case 'unlinked': return 'Oportunidad sin proyecto Redmine enlazado'
        default: return 'Sin datos de Redmine'
    }
}

/**
 * Text for the real-hours line of a day cell: the logged hours, "0" for a
 * past working day with nothing logged, and "" when unknown or in the future.
 */
export const realCellText = (row: RedmineRowFields, day: string, asOf?: string | null): string => {
    if (!row.real_available) return ''
    const real = row.daily_real?.[day]
    if (real && real > 0) return real.toFixed(2)
    if (asOf && day <= asOf) return '0'
    return ''
}

/** Sum of real hours for one day across rows (user header rows). */
export const sumReal = (rows: RedmineRowFields[], day: string): number =>
    rows.reduce((sum, row) => sum + (row.daily_real?.[day] || 0), 0)

/** Sum of real totals across rows. */
export const sumRealTotal = (rows: RedmineRowFields[]): number =>
    rows.reduce((sum, row) => sum + (row.total_real || 0), 0)
